import {createMuiTheme, fade} from '@material-ui/core';

const palette = {
  common: {
    black: '#000000',
    white: '#ffffff'
  },
  primary: {
    main: '#0F2147',
    dark: '#003b69'
  },
  secondary: {
    main: '#5CBCA9'
  },
  error: {
    main: '#C15959'
  },
  success: {
    main: '#169368'
  },
  info: {
    main: '#50B8C1'
  },
  warning: {
    main: '#DD9B2B'
  },
  buttonTertiary: {
    primary: '#E35F52',
    dark: '#df473a'
  },
  text: {
    primary: fade('#000000', 0.87),
    secondary: fade('#000000', 0.54),
    disabled: fade('#000000', 0.38)
  },
  grey: {
    100: fade('#ffffff', 0.38),
    200: fade('#ffffff', 0.54),
    300: fade('#ffffff', 0.7),
    400: fade('#000000', 0.04),
    500: fade('#000000', 0.12),
    600: fade('#000000', 0.38),
    700: fade('#000000', 0.54),
    800: fade('#000000', 0.87)
  },
  background: {
    paper: '#ffffff',
    default: fade('#000000', 0.04)
  },
  divider: fade('#000000', 0.12)
};

const typography = {
  fontFamily: 'Lato',
  h1: {
    fontSize: '100px',
    fontWeight: 300,
    lineHeight: '120px',
    letterSpacing: '0px'
  },
  h2: {
    fontSize: '62px',
    fontWeight: 300,
    lineHeight: '74px',
    letterSpacing: '0px'
  },
  h3: {
    fontSize: '50px',
    fontWeight: 400,
    lineHeight: '60px',
    letterSpacing: '0px'
  },
  h4: {
    fontSize: '35px',
    fontWeight: 400,
    lineHeight: '42px',
    letterSpacing: '0px'
  },
  h5: {
    fontSize: '25px',
    fontWeight: 400,
    lineHeight: '30px',
    letterSpacing: '0px'
  },
  h6: {
    fontSize: '21px',
    fontWeight: 500,
    lineHeight: '25px',
    letterSpacing: '0px'
  },
  body1: {
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: '21px',
    letterSpacing: '0px'
  },
  body2: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '0px'
  },
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '15px',
    letterSpacing: '0px'
  },
  overline: {
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '12px',
    letterSpacing: '0px',
    textTransform: 'uppercase'
  }
};

const theme = createMuiTheme({
  palette,
  typography
});

theme.props = {
  MuiButton: {
    disableRipple: true,
    disableElevation: true
  }
};

theme.overrides = {
  MuiCssBaseline: {
    '@global': {
      body: {
        backgroundColor: theme.palette.common.white
      }
    }
  },
  MuiButton: {
    root: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingRight: theme.spacing(2.75),
      paddingLeft: theme.spacing(2.75),
      borderRadius: theme.spacing(1.5),
      textTransform: 'unset',
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight
    },
    contained: {
      backgroundColor: theme.palette.buttonTertiary.primary,
      color: theme.palette.common.white,
      '&.Mui-disabled': {
        color: theme.palette.common.white,
        backgroundColor: fade(theme.palette.buttonTertiary.primary, 0.38)
      },
      '&:hover': {
        backgroundColor: theme.palette.buttonTertiary.dark
      }
    },
    textSecondary: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingRight: theme.spacing(2.75),
      paddingLeft: theme.spacing(2.75),
      '&.Mui-disabled': {
        color: fade(theme.palette.secondary.main, 0.38)
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[500]
      }
    },
    outlined: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingRight: theme.spacing(2.75),
      paddingLeft: theme.spacing(2.75),
      color: theme.palette.common.white,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: theme.palette.common.white,
      backgroundColor: 'transparent',
      '&.Mui-disabled': {
        color: theme.palette.grey[100],
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: theme.palette.common.white
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[400]
      }
    }
  },
  MuiIconButton: {
    root: {
      '&:hover': {
        backgroundColor: theme.palette.buttonTertiary.dark
      },
      '&.Mui-disabled': {
        color: theme.palette.common.white,
        backgroundColor: fade(theme.palette.buttonTertiary.primary, 0.38)
      },
      '&.MuiIconButton-transparentBg': {
        color: theme.palette.common.white,
        backgroundColor: 'transparent',
        '&.Mui-disabled': {
          color: theme.palette.grey[200],
          backgroundColor: 'transparent'
        },
        '&:hover': {
          backgroundColor: theme.palette.grey[400]
        }
      }
    },
    sizeSmall: {
      '& .MuiSvgIcon-root': {
        width: theme.spacing(3),
        height: theme.spacing(3)
      }
    },
    colorPrimary: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.buttonTertiary.primary,
      '&:hover': {
        backgroundColor: theme.palette.buttonTertiary.dark
      },
      '&.Mui-disabled': {
        color: theme.palette.common.white,
        backgroundColor: fade(theme.palette.buttonTertiary.primary, 0.38)
      }
    },
    colorSecondary: {
      color: theme.palette.secondary.main,
      '&.Mui-disabled': {
        color: fade(theme.palette.secondary.main, 0.38),
        backgroundColor: 'transparent'
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[500]
      }
    }
  },
  MuiCheckbox: {
    root: {
      color: theme.palette.text.disabled,
      '&.MuiIconButton-colorSecondary': {
        '&:hover': {
          backgroundColor: 'unset'
        },
        '&.Mui-disabled': {
          color: theme.palette.divider
        }
      },
      '&.Mui-checked': {
        '&:hover': {
          backgroundColor: 'unset !important'
        }
      }
    }
  },
  MuiFab: {
    root: {
      zIndex: 1,
      backgroundColor: theme.palette.buttonTertiary.primary,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.buttonTertiary.dark
      }
    },
    sizeSmall: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      minHeight: 'unset',
      '& .MuiSvgIcon-root': {
        width: theme.spacing(2.25),
        height: theme.spacing(2.25)
      }
    },
    secondary: {
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.16)}`,
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.grey[500]
      }
    }
  },
  MuiLink: {
    root: {
      color: `${fade(theme.palette.common.black, 0.54)}`,
      '&:hover': {
        color: `${fade(theme.palette.common.black, 0.87)}`
      },
      '&.MuiLink-disabled': {
        color: `${fade(theme.palette.common.black, 0.38)}`
      }
    }
  },
  MuiFormControl: {
    root: {
      '& .MuiFormLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
          color: theme.palette.secondary.main
        }
      },
      '& .MuiInputLabel-outlined': {
        color: theme.palette.text.primary,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        height: theme.spacing(7),
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          }
        },
        '&.Mui-error': {
          '& .MuiOutlinedInput-notchedOutline': {
            '&:hover': {
              borderColor: theme.palette.error.main
            }
          }
        },
        '& .MuiOutlinedInput-input': {
          paddingTop: theme.spacing(2.5),
          paddingBottom: theme.spacing(2.25),
          paddingLeft: theme.spacing(2),
          color: theme.palette.text.primary,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight
        },
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          }
        },
        '&.Mui-disabled': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[400]
          },
          '& .MuiOutlinedInput-input': {
            color: theme.palette.text.disabled
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.grey[400]
            }
          }
        },
        '& .MuiSelect-outlined': {
          paddingRight: 0,
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.spacing(1.5)
        },
        '& .MuiSelect-select': {
          '&:focus': {
            backgroundColor: theme.palette.common.white
          }
        },
        '& .MuiSelect-icon': {
          color: theme.palette.text.primary,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled
          }
        }
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: theme.palette.grey[400]
      },
      '& .MuiInputLabel-shrink': {
        color: theme.palette.text.secondary
      },
      '&.MuiTextField-nolabel': {
        '& .MuiFormLabel-root': {
          '&.Mui-focused': {
            display: 'none'
          },
          '&.MuiInputLabel-outlined': {
            transform: 'translate(14px, 12px) scale(1)'
          }
        },
        '& .MuiInputLabel-outlined': {
          color: theme.palette.primary.main
        },
        '& .PrivateNotchedOutline-legendNotched-55': {
          display: 'none'
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: theme.palette.divider
        },
        '& .MuiOutlinedInput-root': {
          height: theme.spacing(5),
          width: 143,
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.divider
            }
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
              borderWidth: 1,
              backgroundColor: theme.palette.divider
            }
          },
          [theme.breakpoints.down('xs')]: {
            width: 137
          },
          '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: 20,
            color: theme.palette.primary.main
          }
        },
        '& .MuiSelect-iconOutlined': {
          right: 12
        }
      }
    }
  },
  MuiMenu: {
    paper: {
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.16)}`,
      width: 'auto'
    }
  },
  MuiMenuItem: {
    root: {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: theme.typography.body2.fontWeight,
      '&:hover': {
        backgroundColor: theme.palette.grey[400]
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 'unset'
      }
    }
  },
  MuiListItem: {
    root: {
      '&.Mui-selected': {
        backgroundColor: theme.palette.grey[400],
        '&:hover': {
          backgroundColor: theme.palette.grey[400]
        }
      }
    }
  },
  MuiTooltip: {
    tooltip: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.spacing(1.5),
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.16)}`,
      color: theme.palette.text.secondary
    }
  },
  MuiCardContent: {
    root: {
      padding: 0,
      '&:last-child': {
        paddingBottom: 0
      }
    }
  },
  MuiTab: {
    root: {
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.h6.fontWeight,
      lineHeight: theme.typography.h6.lineHeight,
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: theme.spacing(3),
      paddingLeft: 0,
      textTransform: 'none',
      minHeight: 'unset',
      minWidth: 'unset !important',
      '&:not(:first-child)': {
        borderLeftStyle: 'solid',
        borderLeft: 1,
        borderLeftColor: theme.palette.grey[400],
        paddingLeft: theme.spacing(3)
      },
      '&:hover': {
        color: theme.palette.text.primary
      },
      '&.Mui-selected': {
        cursor: 'initial'
      },
      '&.MuiTab-textColorSecondary.Mui-selected': {
        color: theme.palette.common.black
      }
    }
  },
  MuiTabs: {
    root: {
      alignItems: 'center',
      minHeight: 'unset',
      '&.MuiTabs-calendarDay': {
        backgroundColor: theme.palette.primary.main,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
          width: '100%'
        },
        [theme.breakpoints.down('xs')]: {
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1)
        },
        paddingTop: 18,
        paddingBottom: 18,
        borderRadius: 12,
        '& .MuiTab-root': {
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          lineHeight: theme.typography.body2.lineHeight,
          '&:not(:first-child)': {
            borderLeftStyle: 'solid',
            borderLeft: 1,
            borderLeftColor: '#2C3C5D',
            paddingLeft: theme.spacing(3)
          },
          '& .MuiTab-wrapper': {
            flexDirection: 'row',
            color: theme.palette.common.white,
            '&:hover': {
              color: theme.palette.common.white
            }
          },
          '&.MuiTab-labelIcon': {
            minHeight: 'unset',
            paddingTop: 0,
            '&:last-child': {
              paddingRight: 0
            },
            '& .MuiTab-wrapper': {
              '& > *:first-child': {
                marginBottom: 0,
                marginRight: 4
              }
            }
          },
          '& .MuiTouchRipple-root': {
            display: 'none'
          }
        },
        '& .MuiTabs-flexContainer': {
          justifyContent: 'center',
          [theme.breakpoints.down('md')]: {
            justifyContent: 'space-evenly'
          }
        }
      }
    }
  },
  PrivateTabIndicator: {
    root: {
      display: 'none'
    }
  },
  MuiTabScrollButton: {
    root: {
      display: 'none'
    }
  },
  MuiAppBar: {
    root: {
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.16)}`,
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5)
    },
    colorPrimary: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.secondary.main
    }
  },
  MuiBadge: {
    anchorOriginTopRightRectangle: {
      backgroundColor: theme.palette.warning.main,
      top: -5,
      right: -5,
      color: theme.palette.common.white
    },
    badge: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      minWidth: 'unset',
      fontSize: theme.typography.overline.fontSize
    }
  },
  MuiToolbar: {
    regular: {
      minHeight: 0,
      [theme.breakpoints.up('xs')]: {
        minHeight: 0
      }
    }
  },
  // MuiSnackbar: {
  //   anchorOriginTopCenter: {
  //     [theme.breakpoints.up('md')]: {
  //       left: '66%'
  //     }
  //   }
  // },
  MuiSnackbarContent: {
    root: {
      color: theme.palette.common.white,
      padding: 0,
      fontSize: theme.typography.body2.fontSize,
      borderRadius: 10,
      boxShadow: `0 4px 10px ${fade(theme.palette.common.black, 0.16)}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(7)
    },
    message: {
      padding: 0,
      '& .MuiIconButton-root': {
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    }
  },
  MuiStepper: {
    root: {
      paddingTop: theme.spacing(2),
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0
    },
    moreSteps: {
      '& .MuiStep-horizontal': {
        paddingLeft: 0,
        paddingRight: 0,
        marginLeft: '48px ',
        marginRight: 58
      }
    }
  },
  MuiStep: {
    root: {
      '& .MuiSvgIcon-root': {
        color: palette.common.white,
        '&.MuiStepper-parkingIcon': {
          backgroundColor: palette.text.disabled,
          color: palette.common.white,
          borderRadius: 4,
          padding: 4,
          width: 16,
          height: 16
        }
      }
    },
    alternativeLabel: {
      flex: 'none'
    },
    horizontal: {
      marginBottom: -20,
      [theme.breakpoints.down('xs')]: {
        marginLeft: 9,
        marginRight: 25
      },
      [theme.breakpoints.up('sm')]: {
        // marginLeft: 84,
        // marginRight: 126
        marginLeft: 22,
        marginRight: 76
      },
      [theme.breakpoints.up('md')]: {
        // marginLeft: 155,
        // marginRight: 213
        marginLeft: 35,
        marginRight: 76
      }
    },
    completed: {
      '& .MuiSvgIcon-root': {
        color: '#90D1C4',
        '&.MuiStepper-parkingIcon': {
          backgroundColor: '#90D1C4',
          color: palette.common.white,
          borderRadius: 4,
          padding: 4,
          width: 16,
          height: 16
        }
      }
    }
  },
  MuiStepLabel: {
    root: {
      alignItems: 'unset',
      '&.MuiStepLabel-alternativeLabel': {
        flexDirection: 'row'
      }
    },
    label: {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.body1.fontWeight,
      '&.MuiStepLabel-alternativeLabel': {
        marginTop: 1,
        marginLeft: theme.spacing(1.5)
      },
      '&.MuiStepLabel-completed': {
        color: '#5C6882'
      },
      '&.MuiStepLabel-active': {
        color: theme.palette.primary.main
      },
      '&.Mui-disabled': {
        color: theme.palette.text.disabled
      }
    },
    iconContainer: {
      color: theme.palette.secondary.main,
      '&.MuiStepLabel-alternativeLabel': {
        color: theme.palette.secondary.main,
        marginBottom: 15
      }
    },
    horizontal: {
      '& .MuiStepLabel-iconContainer': {
        '&.MuiStepLabel-alternativeLabel': {
          marginBottom: 15
        }
      }
    }
  },
  MuiStepButton: {
    root: {
      '&.Mui-disabled': {
        '& .MuiStepLabel-iconContainer.MuiStepLabel-alternativeLabel': {
          color: theme.palette.text.disabled
        }
      }
    },
    vertical: {
      padding: 31,
      paddingLeft: 0,
      margin: -10
    },
    touchRipple: {
      color: 'transparent'
    }
  },
  MuiStepConnector: {
    lineVertical: {
      position: 'absolute',
      top: -44,
      left: 26,
      height: 42,
      [theme.breakpoints.up('lg')]: {
        display: 'none'
      }
    },
    lineHorizontal: {
      width: 24,
      position: 'absolute',
      left: `calc(-50% + -77px)`,
      [theme.breakpoints.up('md')]: {
        left: `calc(-50% + -63px)`,
        marginLeft: -53
      },
      [theme.breakpoints.down('sm')]: {
        left: `calc(-50% + -13px)`
      },
      [theme.breakpoints.down('xs')]: {
        left: `calc(-50% + -77px)`
      }
    },
    alternativeLabel: {
      [theme.breakpoints.down('xs')]: {
        left: `calc(-50% + 119px)`
      }
    }
  },
  MuiTableContainer: {
    root: {
      borderRadius: theme.spacing(3),
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.08)}`,
      [theme.breakpoints.down('md')]: {
        boxShadow: 'none',
        borderRadius: 0,
        overflowX: 'hidden'
      }
    }
  },
  MuiTableCell: {
    root: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
      borderBottomStyle: 'solid',
      borderBottomWidth: 1,
      borderBottomColor: fade(theme.palette.common.black, 0.12),
      '&:last-child': {
        [theme.breakpoints.down('xs')]: {
          paddingTop: theme.spacing(6)
        }
      }
    }
  },
  MuiTableRow: {
    root: {
      '&:hover': {
        backgroundColor: fade(theme.palette.common.black, 0.04)
      },
      '&:first-child': {
        '& .MuiTableCell-root': {
          borderTopStyle: 'solid',
          borderTopWidth: 1,
          borderTopColor: fade(theme.palette.common.black, 0.12)
        }
      }
    }
  },
  MuiPaper: {
    rounded: {
      borderRadius: theme.spacing(1.5)
    }
  },
  MuiList: {
    root: {
      '&:focus': {
        outline: 0
      }
    }
  },
  MuiListItemText: {
    primary: {
      fontSize: theme.typography.body2.fontSize
    },
    secondary: {
      marginLeft: theme.spacing(-11),
      fontSize: theme.typography.caption.fontSize,
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(-5)
      }
    }
  },
  MuiListItemAvatar: {
    root: {
      minWidth: 0,
      marginRight: theme.spacing(1),
      '& .MuiAvatar-root': {
        width: theme.spacing(3),
        height: theme.spacing(3)
      }
    }
  },
  MuiDialogTitle: {
    root: {
      padding: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3)
      }
    }
  },
  MuiDialogContent: {
    root: {
      paddingTop: 6,
      paddingBottom: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingLeft: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        paddingBottom: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3)
      },
      [theme.breakpoints.down('xs')]: {
        '& .MuiCard-root': {
          minWidth: 'unset',
          width: '100%'
        }
      }
    }
  },
  MuiDialogActions: {
    root: {
      paddingTop: 0,
      paddingBottom: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingLeft: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        paddingTop: 0,
        paddingBottom: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3)
      }
    }
  },
  MuiDrawer: {
    paperAnchorRight: {
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    }
  },
  MuiAutoComplete: {
    root: {
      '& .MuiAutoComplete-endAdornment': {
        '& .MuiIconButton-root': {
          '&:hover': {
            backgroundColor: 'transparent !important'
          }
        }
      }
    }
  },
  MuiSwitch: {
    root: {
      '& .MuiIconButton-root:hover': {
        backgroundColor: 'initial'
      }
    },
    thumb: {
      boxShadow: `0 5px 10px ${fade(theme.palette.common.black, 0.16)}`
    },
    switchBase: {
      color: theme.palette.common.white,
      '&.Mui-checked': {
        '& + .MuiSwitch-track': {
          opacity: 0.3
        }
      }
    },
    colorSecondary: {
      '&.Mui-checked': {
        '&:hover': {
          backgroundColor: 'transparent !important'
        }
      }
    }
  },
  MuiPickersCalendarHeader: {
    switchHeader: {
      display: 'none'
    },
    daysHeader: {
      borderBottomStyle: 'solid',
      borderBottomWidth: 1,
      borderBottomColor: fade(theme.palette.common.black, 0.12)
    },
    dayLabel: {
      marginBottom: theme.spacing(4),
      marginRight: theme.spacing(0.5),
      marginLeft: theme.spacing(0.5),
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight,
      fontWeight: theme.typography.body2.fontWeight,
      color: fade(theme.palette.common.black, 0.54),
      [theme.breakpoints.down('md')]: {
        marginLeft: 31,
        marginRight: 17
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5)
      },
      '@media (min-width: 600px) and (max-width: 764px)': {
        marginRight: 2,
        marginLeft: 2
      }
    }
  },
  MuiPickersDay: {
    day: {
      marginRight: theme.spacing(0.5),
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      '&:hover': {
        backgroundColor: '#F5F5F5'
      },
      [theme.breakpoints.down('md')]: {
        marginLeft: 28,
        marginRight: 11
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(0.5),
        marginRight: 2
      },
      '@media (min-width: 600px) and (max-width: 764px)': {
        marginRight: 2,
        marginLeft: 2
      }
    },
    current: {
      color: theme.palette.secondary.main,
      fontWeight: 400
    },
    daySelected: {
      backgroundColor: theme.palette.secondary.main,
      fontWeight: 400,
      '&:hover': {
        backgroundColor: theme.palette.secondary.main
      }
    }
  },
  MuiPickersBasePicker: {
    pickerView: {
      overflowY: 'hidden',
      minHeight: 300,
      [theme.breakpoints.down('md')]: {
        width: 528,
        maxWidth: 'unset',
        minWidth: 'unset'
      },
      [theme.breakpoints.down('xs')]: {
        width: 'unset'
      },
      '@media (min-width: 600px) and (max-width: 764px)': {
        width: 'unset'
      }
    },
    containerLandscape: {
      justifyContent: 'center'
    }
  },
  MuiAvatar: {
    root: {
      '& .MuiSvgIcon-root': {
        fill: '#989898'
      }
    },
    colorDefault: {
      backgroundColor: '#EBEBEB'
    }
  },
  MuiChip: {
    outlined: {
      '&.MuiChip-greenOutline': {
        borderColor: '#E3F5F2',
        borderStyle: 'solid',
        borderWidth: 1,
        backgroundColor: '#EAF8F5',
        borderRadius: 21,
        '& .MuiChip-label': {
          color: theme.palette.primary.main,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: 600,
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 14,
          paddingRight: 14
        }
      }
    }
  }
};

export default theme;
